// src/hooks/useGeocodeSSE.js
import { useCallback, useRef, useState } from 'react';
import {
    openSSE,
    startGeocodeFile,
    startGeocodeFileAt,
    downloadResultFile,
    downloadResultFileAt
} from 'features/geocoder/services/geocodeApi';

import { useGeoCodingContext } from '../context/GeoCodingContext';

export default function useGeocodeSSE(onComplete) {
    const {
        setIsFileDeletedAfterUpload
    } = useGeoCodingContext();

    const [progress, setProgress] = useState(-1);
    const [status, setStatus] = useState('');
    const [downloading, setDownloading] = useState(false);
    const esRef = useRef(null);

    const closeES = () => {
        try { esRef.current?.close(); } catch { }
        esRef.current = null;
    };

    const cancel = useCallback(() => {
        closeES();
        setProgress(-1);
        setStatus('');
        setDownloading(false);
    }, []);

    const parseContentDisposition = (cd) => {
        if (!cd) return { fileName: undefined, fileNameNoExt: undefined, ext: undefined };
        const m = /filename\*?=(?:UTF-8''|")?([^;\r\n"]+)/i.exec(cd);
        let fileName = m ? decodeURIComponent(m[1].replace(/"/g, '')) : undefined;
        if (!fileName) return { fileName, fileNameNoExt: undefined, ext: undefined };
        const dot = fileName.lastIndexOf('.');
        const ext = dot > -1 ? fileName.slice(dot + 1).toLowerCase() : undefined;
        const fileNameNoExt = dot > -1 ? fileName.slice(0, dot) : fileName;
        return { fileName, fileNameNoExt, ext };
    };

    /**
     * @param {FormData} formData
     * @param {{
     *   uploadUrl?: string,      // 업로드 엔드포인트
     *   sseUrl?: string,         // SSE 베이스(여기에 ?time= 붙음)
     *   downloadUrl?: string,    // 다운로드 엔드포인트
     *   autoDownload?: boolean
     * }} opts
     */
    const run = useCallback(async (formData, opts = {}) => {
        const {
            uploadUrl,                 // 예: '/api/reverse/geoCodeByFile'
            sseUrl,                    // 예: '/api/reverse/geoCodeByFileStatus'
            downloadUrl,               // 예: '/api/reverse/downloadFile'
            autoDownload = false,
        } = opts;

        setProgress(0);
        setStatus('파일 업로드를 시작합니다.');
        setDownloading(false);

        const time = String(Date.now());
        formData.append('time', time);

        const sseEndpoint = (sseUrl || '/api/geoCodeByFileStatus') + `?time=${time}`;
        const es = openSSE(sseEndpoint);
        esRef.current = es;

        es.addEventListener('progress', (e) => {
            const v = parseInt(e.data, 10);
            if (!Number.isNaN(v)) setProgress(v);
        });

        es.onmessage = async (e) => {
            const msg = e.data || '';

            if (msg.startsWith('message:')) {
                //console.log(msg.replace('message:', '').trim());
                setStatus(msg.replace('message:', '').trim());
                return;
            }

            if (msg.startsWith('complete:')) {
                const serverFileName = msg.slice('complete:'.length).trim();

                // 모달 닫고 후처리
                // setProgress(-1); // ☑️☑️ 주석 처리
                // setStatus('');
                closeES();

                setDownloading(true);
                try {
                    // 다운로드
                    const resp = downloadUrl
                        ? await downloadResultFileAt(downloadUrl, serverFileName)
                        : await downloadResultFile(serverFileName);

                    const blob = resp.data;
                    const contentType = resp.headers?.['content-type'];
                    const contentDisposition = resp.headers?.['content-disposition'];
                    const { fileName, fileNameNoExt, ext } = parseContentDisposition(contentDisposition);

                    onComplete?.(blob, {
                        contentType,
                        contentDisposition,
                        fileName: fileName || serverFileName,
                        fileNameNoExt,
                        ext,
                    });

                    if (autoDownload) {
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = fileName || serverFileName;
                        document.body.appendChild(a);
                        a.click();
                        a.remove();
                        URL.revokeObjectURL(url);
                    }

                    setProgress(100);
                    //setStatus('지오코딩이 완료되었습니다.');
                    setIsFileDeletedAfterUpload(false);
                    console.log("지오코딩 완료")
                } catch (err) {
                    console.error(err);
                    setStatus('결과 다운로드 중 오류가 발생했습니다.');
                } finally {
                    setDownloading(false);
                }
                return;
            }

            const v = parseInt(msg, 10);
            if (!Number.isNaN(v)) {
                setProgress(v);
                if (v === 5) setStatus('지오코딩이 진행중입니다.');
                if (v === 95) setStatus('결과 파일을 준비 중입니다.');
            }
        };

        es.onerror = () => {
            closeES();
            setProgress(-1);
            setStatus('진행 중 연결이 종료되었습니다.');
        };

        try {
            if (uploadUrl) {
                await startGeocodeFileAt(uploadUrl, formData);
            } else {
                await startGeocodeFile(formData);
            }
        } catch (err) {
            console.error(err);
            setStatus('업로드 중 오류가 발생했습니다.');
            setProgress(-1);
            closeES();
        }
    }, [onComplete]);

    return { progress, setProgress, status, downloading, run, cancel };
}
